import * as React from 'react'
import {
  Box,
  Typography,
  Button,
  Radio,
  RadioGroup,
  FormControl,
  FormControlLabel,
  FormLabel,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Divider,
} from '@mui/material'
import { useGetIdentity, useNotify } from 'react-admin'
import { client } from '../../providers/data/client'

const RESULT_SCOPES = [
  { value: 'both', label: 'Receive email for both positive and negative results' },
  { value: 'positiveOnly', label: 'Receive email for positive results only' },
  { value: 'none', label: 'Do not receive notifications' },
]

const RESULT_SUMMARIES = [
  { value: 'daily', label: 'Receive daily' },
  { value: 'weekly', label: 'Receive weekly' },
]

const NotificationSettings = () => {
  const notify = useNotify()
  const { identity } = useGetIdentity()
  const userEmail = identity?.email || localStorage.getItem('provider_portal_customer_email') || ''

  const [isLoading, setIsLoading] = React.useState(true)
  const [isSaving, setIsSaving] = React.useState(false)
  const [loadError, setLoadError] = React.useState('')

  const [resultScope, setResultScope] = React.useState('none')
  const [resultSummary, setResultSummary] = React.useState('daily')
  const [providers, setProviders] = React.useState([])

  // Frequency is meaningless when the user has opted out entirely.
  const notificationsDisabled = resultScope === 'none'

  React.useEffect(() => {
    let isMounted = true

    const fetchSettings = async () => {
      setIsLoading(true)
      setLoadError('')
      try {
        const response = await client().get('/provider-portal/notification-settings')
        if (!isMounted) return

        const data = response.data || {}
        setResultScope(data.resultScope || 'none')
        setResultSummary(data.resultSummary || 'daily')
        setProviders(data.providers || [])
      } catch (err) {
        console.error('Error fetching notification settings:', err)
        if (isMounted) {
          setLoadError(err.response?.data?.message || 'Failed to load your notification settings.')
        }
      } finally {
        if (isMounted) setIsLoading(false)
      }
    }

    fetchSettings()

    return () => {
      isMounted = false
    }
  }, [])

  const handleProviderToggle = (providerId, field) => (event) => {
    const checked = event.target.checked
    setProviders((current) =>
      current.map((provider) => (provider.id === providerId ? { ...provider, [field]: checked } : provider))
    )
  }

  const handleSave = async () => {
    setIsSaving(true)
    try {
      await client().put('/provider-portal/notification-settings', {
        resultScope,
        resultSummary,
        providers: providers.map((provider) => ({
          id: provider.id,
          faxPositiveResults: !!provider.faxPositiveResults,
          faxNegativeResults: !!provider.faxNegativeResults,
        })),
      })
      notify('Notification settings saved', { type: 'success' })
    } catch (err) {
      console.error('Error saving notification settings:', err)
      notify(err.response?.data?.message || 'Failed to save notification settings', { type: 'error' })
    } finally {
      setIsSaving(false)
    }
  }

  if (isLoading) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    )
  }

  if (loadError) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>
          Notification Settings
        </Typography>
        <Typography color="error">{loadError}</Typography>
      </Box>
    )
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Notification Settings
      </Typography>

      {/* ---------------- Your Notifications ---------------- */}
      {/* Preferences for the signed-in provider access user. */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h5" gutterBottom>
          Your Notifications
          {userEmail ? (
            <>
              {' ('}
              <Box
                component="span"
                sx={{
                  fontFamily: "'Courier New', Courier, ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', monospace",
                  backgroundColor: '#f3f3f3',
                  px: 0.75,
                  py: 0.25,
                  borderRadius: 0.75,
                }}
              >
                {userEmail}
              </Box>
              {')'}
            </>
          ) : null}
        </Typography>
        <Typography paragraph color="text.secondary">
          Choose which results you would like emailed to you, and how often. Summaries are only sent when there is
          something new to report.
        </Typography>

        <FormControl sx={{ mb: 3, display: 'block' }}>
          <FormLabel id="result-scope-label">Results</FormLabel>
          <RadioGroup
            aria-labelledby="result-scope-label"
            value={resultScope}
            onChange={(event) => setResultScope(event.target.value)}
          >
            {RESULT_SCOPES.map((scope) => (
              <FormControlLabel key={scope.value} value={scope.value} control={<Radio />} label={scope.label} />
            ))}
          </RadioGroup>
        </FormControl>

        <FormControl sx={{ display: 'block' }} disabled={notificationsDisabled}>
          <FormLabel id="result-summary-label">Frequency</FormLabel>
          <RadioGroup
            aria-labelledby="result-summary-label"
            value={resultSummary}
            onChange={(event) => setResultSummary(event.target.value)}
          >
            {RESULT_SUMMARIES.map((summary) => (
              <FormControlLabel
                key={summary.value}
                value={summary.value}
                control={<Radio />}
                label={summary.label}
                disabled={notificationsDisabled}
              />
            ))}
          </RadioGroup>
        </FormControl>
      </Box>

      <Divider sx={{ mb: 4 }} />

      {/* ---------------- Provider Notifications ---------------- */}
      {/* Fax preferences belonging to each provider this user can access. */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h5" gutterBottom>
          Provider Notifications
        </Typography>
        <Typography paragraph color="text.secondary">
          Choose which results are faxed to each provider you represent.
        </Typography>

        {providers.length === 0 ? (
          <Typography color="text.secondary">There are no providers associated with your account.</Typography>
        ) : (
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Provider</TableCell>
                  <TableCell>Fax</TableCell>
                  <TableCell align="center">Fax Positive Results</TableCell>
                  <TableCell align="center">Fax Negative Results</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {providers.map((provider) => (
                  <TableRow key={provider.id}>
                    <TableCell>
                      {provider.displayName}
                      {provider.clinicName ? ` (${provider.clinicName})` : ''}
                    </TableCell>
                    <TableCell>{provider.fax || '-'}</TableCell>
                    <TableCell align="center">
                      <Checkbox
                        checked={!!provider.faxPositiveResults}
                        onChange={handleProviderToggle(provider.id, 'faxPositiveResults')}
                        inputProps={{ 'aria-label': `Fax positive results for ${provider.displayName}` }}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Checkbox
                        checked={!!provider.faxNegativeResults}
                        onChange={handleProviderToggle(provider.id, 'faxNegativeResults')}
                        inputProps={{ 'aria-label': `Fax negative results for ${provider.displayName}` }}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>

      <Button
        variant="contained"
        onClick={handleSave}
        disabled={isSaving}
        sx={{
          textTransform: 'none',
          fontSize: '15px',
          fontWeight: 600,
          py: 1.2,
          px: 3,
          backgroundColor: 'rgb(53, 171, 213)',
          border: '1px solid rgb(12, 66, 88)',
          color: '#FFFFFF',
          '&:hover': { backgroundColor: 'rgb(12, 66, 88)' },
          boxShadow: 'none',
        }}
      >
        {isSaving ? 'Saving...' : 'Save Settings'}
      </Button>
    </Box>
  )
}

export default NotificationSettings
